export const MIN_BRIDGE_VERSION = '1.12.0';
export const LAUNCH_WAIT_MS = 30_000;
export const RECHECK_WAIT_MS = 10_000;

export interface BridgeLaunchAck {
  code: string;
  version: string | null;
  at: string;
}

export type LaunchPhase = 'launch' | 'recheck';

export type LaunchState =
  | { kind: 'idle' }
  | { kind: 'waiting' }
  | { kind: 'checkIn' }
  | { kind: 'rechecking' }
  | { kind: 'connected' }
  | { kind: 'outdated'; version: string | null }
  | { kind: 'reinstall' };

const VERSION_PATTERN = /^\d+(\.\d+)*$/;

function versionParts(version: string): number[] {
  return version.split('.').map((part) => parseInt(part, 10));
}

export function compareVersions(a: string, b: string): number {
  const left = versionParts(a);
  const right = versionParts(b);
  const length = Math.max(left.length, right.length);
  for (let i = 0; i < length; i++) {
    const difference = (left[i] ?? 0) - (right[i] ?? 0);
    if (difference !== 0) return Math.sign(difference);
  }
  return 0;
}

export function isBridgeOutdated(version: string | null | undefined): boolean {
  if (!version || !VERSION_PATTERN.test(version)) return true;
  return compareVersions(version, MIN_BRIDGE_VERSION) < 0;
}

export function createLaunchCode(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type DesktopOs = 'windows' | 'mac' | 'other';

export function desktopOsFromUserAgent(userAgent: string): DesktopOs {
  if (/Windows/i.test(userAgent)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'mac';
  return 'other';
}

export function openBridgeHint(os: DesktopOs): string {
  if (os === 'windows') return 'open it from the Start menu';
  if (os === 'mac') return 'open it from your Applications folder';
  return 'open it on your computer';
}

export function bridgeLaunchUrl(code: string, eventId?: string): string {
  const base = `votebeats://open?launch=${code}`;
  return eventId ? `${base}&event=${encodeURIComponent(eventId)}` : base;
}

export function parseBridgeLaunchAck(raw: unknown): BridgeLaunchAck | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (typeof value.code !== 'string' || typeof value.at !== 'string') return null;
  if (value.version !== undefined && value.version !== null && typeof value.version !== 'string') return null;
  return { code: value.code, version: typeof value.version === 'string' ? value.version : null, at: value.at };
}

export function decideLaunchState(input: {
  codes: string[];
  phase: LaunchPhase;
  clickedAtMs: number | null;
  nowMs: number;
  ack: BridgeLaunchAck | null;
  bridgeLive: boolean;
  liveVersion: string | null;
}): LaunchState {
  const { codes, phase, clickedAtMs, nowMs, ack, bridgeLive, liveVersion } = input;
  if (codes.length === 0 || clickedAtMs === null) return { kind: 'idle' };

  const acked = ack !== null && codes.includes(ack.code);
  if (acked || bridgeLive) {
    const version = acked ? ack.version : liveVersion;
    return isBridgeOutdated(version) ? { kind: 'outdated', version } : { kind: 'connected' };
  }

  const elapsedMs = nowMs - clickedAtMs;
  if (phase === 'launch') {
    return elapsedMs < LAUNCH_WAIT_MS ? { kind: 'waiting' } : { kind: 'checkIn' };
  }
  return elapsedMs < RECHECK_WAIT_MS ? { kind: 'rechecking' } : { kind: 'reinstall' };
}
